import React, { useState } from 'react';
import { Button, Checkbox, Modal, Field, FieldLabel, FieldError } from '../ui';
import type { Lesson } from '@court-sync/shared';

interface CancelSessionModalProps {
  isOpen: boolean;
  lesson: Lesson | null;
  academyId: string;
  sessionDateYmd: string;
  sessionDateLabel: string;
  isLoading?: boolean;
  stackLevel?: 'default' | 'elevated';
  /** When true, staff can opt in to grant a make-up session credit */
  makeupLessonEnabled?: boolean;
  onCancel: () => void;
  onConfirm: (
    reason: string,
    notifyStudents: boolean,
    grantMakeupCredit: boolean
  ) => Promise<void>;
}

export const CancelSessionModal: React.FC<CancelSessionModalProps> = ({
  isOpen,
  lesson,
  academyId,
  sessionDateYmd,
  sessionDateLabel,
  isLoading = false,
  stackLevel = 'default',
  makeupLessonEnabled = false,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [notifyStudents, setNotifyStudents] = useState(true);
  const [grantMakeupCredit, setGrantMakeupCredit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for cancellation');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await onConfirm(reason, notifyStudents, grantMakeupCredit);
      setReason('');
      setNotifyStudents(true);
      setGrantMakeupCredit(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setNotifyStudents(true);
    setGrantMakeupCredit(false);
    setError('');
    onCancel();
  };

  if (!isOpen || !lesson || !academyId || !sessionDateYmd) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Cancel this session" maxWidth="sm" stackLevel={stackLevel}>
      <div>
        <div className="space-y-4 mb-6">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-amber-900 dark:text-amber-100 text-sm">
            <strong>Warning:</strong> This cancels only this date (
            <strong>{sessionDateLabel}</strong>). Other sessions in this lesson stay scheduled unless you
            cancel the full package or term separately.
          </div>

          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-gray-200">Lesson:</strong> {lesson.name}
            </p>
          </div>

          <Field>
            <FieldLabel>Cancellation reason</FieldLabel>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError('');
              }}
              placeholder="Reason shown to families where applicable"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              disabled={submitting || isLoading}
            />
            {error && <FieldError>{error}</FieldError>}
          </Field>

          <div className="flex items-start gap-3">
            <Checkbox
              id="notify-students-session"
              checked={notifyStudents}
              onCheckedChange={(checked) =>
                setNotifyStudents(typeof checked === 'boolean' ? checked : false)
              }
              disabled={submitting || isLoading}
            />
            <label
              htmlFor="notify-students-session"
              className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
            >
              Notify students/guardians
            </label>
          </div>

          {makeupLessonEnabled && (
            <div className="flex items-start gap-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
              <Checkbox
                id="grant-makeup-session-cancel"
                checked={grantMakeupCredit}
                onCheckedChange={(checked) =>
                  setGrantMakeupCredit(typeof checked === 'boolean' ? checked : false)
                }
                disabled={submitting || isLoading}
              />
              <label
                htmlFor="grant-makeup-session-cancel"
                className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer leading-snug"
              >
                Grant a make-up session credit to enrolled students
              </label>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={handleClose} disabled={submitting || isLoading}>
            Close
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={submitting || isLoading}
            isLoading={submitting || isLoading}
          >
            Cancel session
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CancelSessionModal;
